import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* -------------------- Animated Right Panel -------------------- */
function AnimatedPanel() {
  const [scene, setScene] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setScene((s) => (s + 1) % 3), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative w-full h-96 bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900/20 rounded-2xl border-2 border-amber-500/40 overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent pointer-events-none" />
      {scene === 0 && <SportsEconomyGrowth />}
      {scene === 1 && <AfricaStadiumScene />}
      {scene === 2 && <InvestmentFlowScene />}
    </div>
  );
}

/* === Scene 1: Sports Economy Growth Chart === */
function SportsEconomyGrowth() {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    let start;
    const duration = 3500;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setProgress(eased);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const years = ['2025', '2027', '2029', '2031', '2035'];
  const values = [0.5, 1.2, 2.1, 3.5, 5.0];

  return (
    <div className="absolute inset-0 p-8">
      <svg viewBox="0 0 640 320" className="w-full h-full">
        {/* Title */}
        <text x="320" y="35" fill="#fde68a" fontSize="20" fontWeight="900" textAnchor="middle">
          African Sports Economy Growth
        </text>
        <text x="320" y="60" fill="#fbbf24" fontSize="16" fontWeight="700" textAnchor="middle">
          Beyond the Game, Build the Economy
        </text>

        {/* Grid Background */}
        <defs>
          <pattern id="premiumGrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(245,158,11,0.1)" strokeWidth="1" />
          </pattern>
          <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#fde047" stopOpacity="0.3" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        <rect x="60" y="80" width="520" height="200" fill="url(#premiumGrid)" rx="8" />

        {/* Axes */}
        <line x1="80" y1="270" x2="560" y2="270" stroke="#fbbf24" strokeWidth="2" />
        <line x1="80" y1="90" x2="80" y2="270" stroke="#fbbf24" strokeWidth="2" />

        {/* Y-axis labels */}
        <text x="65" y="95" fill="#cbd5e1" fontSize="11" textAnchor="end">5%</text>
        <text x="65" y="165" fill="#cbd5e1" fontSize="11" textAnchor="end">2.5%</text>
        <text x="65" y="235" fill="#cbd5e1" fontSize="11" textAnchor="end">0.5%</text>
        <text x="65" y="275" fill="#cbd5e1" fontSize="11" textAnchor="end">0%</text>

        {/* Animated Line Chart */}
        <path
          d={years.map((year, i) => {
            const x = 120 + (i * 100);
            const maxHeight = 180;
            const y = 270 - (values[i] / 5 * maxHeight * progress);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          }).join(' ')}
          fill="none"
          stroke="#fbbf24"
          strokeWidth="4"
          strokeLinecap="round"
          filter="url(#glow)"
        />

        {/* Data Points */}
        {years.map((year, i) => {
          const x = 120 + (i * 100);
          const maxHeight = 180;
          const y = 270 - (values[i] / 5 * maxHeight * progress);
          const isVisible = progress > (i / years.length);
          
          return isVisible ? (
            <g key={i}>
              <circle cx={x} cy={y} r="8" fill="#fde047" stroke="#0f172a" strokeWidth="2">
                <animate attributeName="r" values="6;9;6" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.2}s`} />
              </circle>
              <text x={x} y="290" fill="#e2e8f0" fontSize="12" fontWeight="600" textAnchor="middle">{year}</text>
              <text x={x} y={y - 15} fill="#fde047" fontSize="13" fontWeight="700" textAnchor="middle">{values[i]}%</text>
            </g>
          ) : null;
        })}

        {/* Target Label */}
        {progress > 0.8 && (
          <text x="440" y="115" fill="#10b981" fontSize="14" fontWeight="900">
            TARGET 2035 ⚽
          </text>
        )}
      </svg>
    </div>
  );
}

/* === Scene 2: Africa Stadium with Growth === */
function AfricaStadiumScene() {
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-emerald-900/30 to-slate-900">
      <svg viewBox="0 0 640 320" className="w-full h-full">
        {/* Title */}
        <text x="320" y="40" fill="#fbbf24" fontSize="22" fontWeight="900" textAnchor="middle">
          Africa's Sports Revolution
        </text>

        {/* Africa Map Outline (Simplified) */}
        <g transform="translate(220, 100)" opacity="0.3">
          <path
            d="M 50 20 Q 40 10, 30 20 L 20 40 L 10 80 Q 5 100, 15 120 L 30 140 Q 50 150, 70 145 L 90 140 L 110 130 Q 120 110, 115 90 L 110 70 Q 100 50, 90 40 L 80 25 Q 70 15, 50 20 Z"
            fill="none"
            stroke="#fbbf24"
            strokeWidth="2"
          />
        </g>

        {/* Stadium Icon */}
        <g transform="translate(250, 120)">
          {/* Stadium base */}
          <ellipse cx="70" cy="100" rx="80" ry="25" fill="rgba(245,158,11,0.15)" stroke="#fbbf24" strokeWidth="2" />
          
          {/* Stadium tiers */}
          <rect x="20" y="65" width="100" height="35" rx="5" fill="rgba(15,23,42,0.8)" stroke="#fbbf24" strokeWidth="2" />
          <rect x="35" y="75" width="70" height="20" rx="3" fill="rgba(16,185,129,0.3)" stroke="#10b981" strokeWidth="2">
            <animate attributeName="fill" values="rgba(16,185,129,0.2);rgba(16,185,129,0.5);rgba(16,185,129,0.2)" dur="2s" repeatCount="indefinite" />
          </rect>
          
          {/* Lights */}
          <circle cx="30" cy="55" r="4" fill="#fde047">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite" />
          </circle>
          <circle cx="70" cy="50" r="5" fill="#fde047">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite" begin="0.5s" />
          </circle>
          <circle cx="110" cy="55" r="4" fill="#fde047">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite" begin="1s" />
          </circle>
        </g>

        {/* Stat Cards */}
        <g>
          <rect x="50" y="230" width="150" height="60" rx="8" fill="rgba(15,23,42,0.9)" stroke="#fbbf24" strokeWidth="2" />
          <text x="125" y="255" fill="#fde68a" fontSize="24" fontWeight="900" textAnchor="middle">200+</text>
          <text x="125" y="275" fill="#cbd5e1" fontSize="12" fontWeight="600" textAnchor="middle">Leaders</text>
        </g>

        <g>
          <rect x="440" y="230" width="150" height="60" rx="8" fill="rgba(15,23,42,0.9)" stroke="#10b981" strokeWidth="2" />
          <text x="515" y="255" fill="#10b981" fontSize="24" fontWeight="900" textAnchor="middle">20 +</text>
          <text x="515" y="275" fill="#cbd5e1" fontSize="12" fontWeight="600" textAnchor="middle">Countries</text>
        </g>
      </svg>
    </div>
  );
}

/* === Scene 3: Investment Flow Animation === */
function InvestmentFlowScene() {
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
      <svg viewBox="0 0 640 320" className="w-full h-full">
        {/* Title */}
        <text x="320" y="35" fill="#fbbf24" fontSize="20" fontWeight="900" textAnchor="middle">
          Investment Transformation
        </text>
        <text x="320" y="58" fill="#cbd5e1" fontSize="14" fontWeight="600" textAnchor="middle">
          From Game to Economic Growth
        </text>

        <defs>
          <linearGradient id="flowGradient" x1="0" x2="1">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>

        {/* Flow Path */}
        <path
          d="M 100 160 L 540 160"
          stroke="rgba(245,158,11,0.2)"
          strokeWidth="4"
          strokeDasharray="10,5"
        />

        {/* Sports Icon (Left) */}
        <g transform="translate(50, 130)">
          <circle cx="50" cy="30" r="35" fill="rgba(245,158,11,0.2)" stroke="#fbbf24" strokeWidth="3" />
          <text x="50" y="40" fontSize="32" textAnchor="middle">⚽</text>
          <text x="50" y="80" fill="#fde68a" fontSize="12" fontWeight="700" textAnchor="middle">SPORTS</text>
        </g>

        {/* Animated Money Flow */}
        {[0, 1, 2, 3].map((i) => (
          <circle key={i} r="6" fill="#fde047">
            <animateMotion
              dur="3s"
              repeatCount="indefinite"
              begin={`${i * 0.7}s`}
              path="M 150 160 L 490 160"
            />
          </circle>
        ))}

        {/* Arrow */}
        <g transform="translate(470, 145)">
          <path d="M 0 15 L 20 15 L 20 5 L 35 20 L 20 35 L 20 25 L 0 25 Z" fill="#10b981">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite" />
          </path>
        </g>

        {/* Economy Icon (Right) */}
        <g transform="translate(490, 130)">
          <circle cx="50" cy="30" r="35" fill="rgba(16,185,129,0.2)" stroke="#10b981" strokeWidth="3" />
          <text x="50" y="40" fontSize="32" textAnchor="middle">📈</text>
          <text x="50" y="80" fill="#10b981" fontSize="12" fontWeight="700" textAnchor="middle">ECONOMY</text>
        </g>

        {/* Stats */}
        <g transform="translate(200, 220)">
          <text x="120" y="20" fill="#fbbf24" fontSize="32" fontWeight="900" textAnchor="middle">💰💰💰</text>
          <text x="120" y="40" fill="#cbd5e1" fontSize="13" fontWeight="600" textAnchor="middle">Investment Potential</text>
        </g>

        {/* Pulsing circles */}
        <circle cx="320" cy="160" r="50" fill="none" stroke="#fbbf24" strokeWidth="2" opacity="0.3">
          <animate attributeName="r" values="30;60;30" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
}

/* ------------------------- Header ------------------------- */
export default function Header({ eventStartISO, eventEndISO }) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [status, setStatus] = useState("upcoming");

  const eventStart = useMemo(() => new Date(eventStartISO), [eventStartISO]);
  const eventEnd = useMemo(() => new Date(eventEndISO), [eventEndISO]);

  const fmt = new Intl.DateTimeFormat("en-GB", {
    weekday: "short", year: "numeric", month: "long", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      if (now < eventStart) {
        const d = +eventStart - +now;
        setStatus("upcoming");
        setCountdown({
          days: Math.floor(d / (1000 * 60 * 60 * 24)),
          hours: Math.floor((d / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((d / 1000 / 60) % 60),
          seconds: Math.floor((d / 1000) % 60),
        });
      } else if (now >= eventStart && now <= eventEnd) {
        setStatus("live");
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        setStatus("ended");
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [eventStart, eventEnd]);

  return (
    <header
      id="home"
      className="pt-32 pb-20 px-4 relative overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: "url('/stadium.png')" }}
    >
      {/* Dark overlay to make content readable */}
      <div className="absolute inset-0 bg-black/60" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold mb-3 leading-tight text-white">
              AfricaMed <span className="text-amber-400">Business</span>{" "}
              <span className="text-yellow-300">Forum</span>
            </h1>
            <p className="text-sm tracking-widest text-slate-300 mb-4">3RD EDITION • 2026</p>

            <div className="mb-6">
              <p className="text-amber-300 font-extrabold uppercase text-sm">Theme</p>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                The African <span className="text-yellow-300">Sports Industry</span>
              </h2>
              <h4>Beyond the Game, Build the Economy </h4>
            </div>

            <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-400/50 rounded-xl p-6 mb-6 text-white">
              {status === "upcoming" && (
                <>
                  <p className="text-sm font-semibold text-amber-300 mb-3 uppercase">Event Starts In</p>
                  <div className="grid grid-cols-4 gap-3">
                    {[ 
                      { v: countdown.days, t: "DAYS", c: "text-amber-400" },
                      { v: countdown.hours, t: "HOURS", c: "text-yellow-300" },
                      { v: countdown.minutes, t: "MINS", c: "text-amber-400" },
                      { v: countdown.seconds, t: "SECS", c: "text-yellow-300" },
                    ].map((b, i) => (
                      <div key={i} className="bg-slate-900/80 rounded-lg p-3 text-center">
                        <div className={`text-3xl font-bold ${b.c}`}>{String(b.v).padStart(2, "0")}</div>
                        <div className="text-xs text-slate-400 mt-1">{b.t}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {status === "live" && (
                <div className="flex items-center gap-3 text-green-400 font-semibold">
                  <span className="inline-flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                  Live now • {fmt.format(eventStart)} to {fmt.format(eventEnd)} (Africa/Casablanca)
                </div>
              )}
              {status === "ended" && (
                <div className="text-slate-300 font-medium">This event has ended. Thank you for your interest.</div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/register')}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-4 px-8 rounded-lg transition transform hover:scale-105 flex items-center justify-center"
              >
                Register Now
                <ArrowRight className="ml-2" size={20} />
              </button>
              <button
                onClick={() => navigate('/about')}
                className="border-2 border-amber-400 hover:bg-amber-400/10 text-white font-bold py-4 px-8 rounded-lg transition"
              >
                Learn More
              </button>
            </div>
          </div>

          <div className="hidden md:block relative">
            <AnimatedPanel />
          </div>
        </div>
      </div>
    </header>
  );
}
